import React, { useRef, useState, useLayoutEffect } from "react";
import ReactDOM from "react-dom";
import styled from "styled-components";
import tooltipArrow from "../../assets/images/tooltipArrow.svg";
import SVG from "../ui/SVG";
import { Property } from "csstype";
import { TText } from "./TableGrid";

const Container = styled.div<{ $justifyContent?: string }>`
  position: static;
  display: flex;
  justify-content: ${({ $justifyContent }) =>
    $justifyContent ? $justifyContent : "center"};
`;

const ContentContainer = styled.div<{
  $shouldShow: "show" | "hide";
  $size?: "default" | "large";
  $customArrowPosition?: "left";
}>`
  background: var(--color-dark-blue-700);
  width: max-content;
  position: absolute;
  z-index: ${({ $shouldShow }) => ($shouldShow === "show" ? "99999" : "-1")};
  padding: 6px 12px;
  border-radius: 8px;
  display: block;
  max-width: ${({ $size }) => ($size === "large" ? "437px" : "250px")};
  text-align: ${({ $size }) => ($size === "large" ? "left" : "center")};
  opacity: ${({ $shouldShow }) => ($shouldShow === "show" ? "1" : "0")};
  p,
  b {
    color: var(--color-light-100);
    font-size: 14px;
    text-align: center;
  }
  p {
    font-family: var(--font-paragraph-default);
  }
  b {
    font-family: var(--font-paragraph-bold);
  }
  svg {
    width: 12px;
    height: 8px;
    position: absolute;
    ${({ $customArrowPosition }) =>
      $customArrowPosition === "left"
        ? `bottom: calc(50% - 6px); left: -8px; transform: rotate(90deg);`
        : `bottom: -6px; left: calc(50% - 6px);`}
  }
`;

export type TTooltipProps = {
  children: JSX.Element;
  text: TText[];
  size?: "large" | "default";
  customTextAlignment?: Property.TextAlign;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  arrowPosition?: "left";
  justifyContent?: string;
};

const Tooltip: React.FC<TTooltipProps> = ({
  children,
  text,
  size,
  customTextAlignment,
  onMouseEnter,
  onMouseLeave,
  arrowPosition,
  justifyContent,
}) => {
  const [shouldShow, setShouldShow] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [position, setPosition] = useState<"top" | "bottom">("top");
  const ref = useRef<HTMLDivElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  const updatePosition = () => {
    if (ref.current && tooltipRef.current) {
      const rect = ref.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const scrollY = window.scrollY || window.pageYOffset;
      const scrollX = window.scrollX || window.pageXOffset;

      // Default: position above
      let top = rect.top + scrollY - tooltipRect.height - 8;
      let positionType: "top" | "bottom" = "top";

      // If tooltip would overflow top, position below
      if (top < 0) {
        top = rect.bottom + scrollY + 8;
        positionType = "bottom";
      }

      setCoords({
        top,
        left: rect.left + scrollX + rect.width / 2 - tooltipRect.width / 2,
      });
      setPosition(positionType);
    }
  };

  useLayoutEffect(() => {
    if (shouldShow) {
      updatePosition();
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);
      return () => {
        window.removeEventListener("scroll", updatePosition, true);
        window.removeEventListener("resize", updatePosition);
      };
    }
  }, [shouldShow]);

  const handleOnMouseEnter = () => {
    if (onMouseEnter) onMouseEnter();
    setShouldShow(true);
  };

  const handleOnMouseLeave = () => {
    if (onMouseLeave) onMouseLeave();
    setShouldShow(false);
  };

  const handleTextRender = (text: TText) => {
    switch (text.style) {
      case "bold":
        return (
          <>
            <b style={{ display: "inline", marginRight: text.marginRight }}>
              {text.text}
            </b>
            {text.shouldBreakLine && <br />}
          </>
        );
      case "default":
        return (
          <>
            <p style={{ display: "inline", marginRight: text.marginRight }}>
              {text.text}
            </p>
            {text.shouldBreakLine && <br />}
          </>
        );
      case "faded":
        return (
          <>
            <span style={{ marginRight: text.marginRight }}>{text.text}</span>
            {text.shouldBreakLine && <br />}
          </>
        );
    }
  };

  return (
    <Container $justifyContent={justifyContent}>
      <div
        ref={ref}
        style={{ width: "100%" }}
        onMouseEnter={handleOnMouseEnter}
        onMouseLeave={handleOnMouseLeave}
      >
        {children}
      </div>
      {shouldShow && ReactDOM.createPortal(
        <ContentContainer
          ref={tooltipRef}
          $shouldShow={shouldShow ? "show" : "hide"}
          $size={size}
          style={{
            position: "absolute",
            top: coords.top,
            left: coords.left,
            textAlign: customTextAlignment,
            pointerEvents: "none",
          }}
          $customArrowPosition={arrowPosition}
        >
          {text.map((obj, index) => (
            <React.Fragment key={index}>{handleTextRender(obj)}</React.Fragment>
          ))}
          {size !== "large" && (
            <SVG
              src={tooltipArrow}
              style={{
                transform: position === "bottom" ? "rotate(180deg)" : undefined,
                top: position === "bottom" ? "-8px" : undefined,
                bottom: position === "top" ? "-6px" : undefined,
              }}
            />
          )}
        </ContentContainer>,
        document.body
      )}
    </Container>
  );
};

export default Tooltip;
